import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Switch } from './ui/switch';
import { getConsent, saveConsent, trackEvent, OPEN_SETTINGS_EVENT } from '../lib/consent';

const btn =
  'px-6 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors border';
const solidBtn = `${btn} bg-ink text-bone border-ink hover:bg-ink/85`;
const ghostBtn = `${btn} bg-transparent text-ink border-ink/30 hover:border-ink`;

const categories = [
  {
    key: 'necessary',
    title: 'Nødvendige',
    text: 'Sikrer at hjemmesiden fungerer og husker dit cookievalg. Kan ikke fravælges.',
    locked: true,
  },
  {
    key: 'analytics',
    title: 'Statistik',
    text: 'Hjælper os med at forstå, hvordan hjemmesiden bruges (Google Analytics 4), så vi kan forbedre den.',
  },
  {
    key: 'marketing',
    title: 'Marketing',
    text: 'Bruges til at måle og målrette annoncering (Google Ads m.fl.).',
  },
];

const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const c = getConsent();
    if (c) {
      setPrefs({ analytics: c.analytics, marketing: c.marketing });
    } else setVisible(true);
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, onOpen);
  }, []);

  const decide = (p) => {
    const hadAnalytics = getConsent()?.analytics;
    saveConsent(p);
    if (p.analytics && !hadAnalytics) {
      trackEvent('kepp_page_view', { page_path: window.location.pathname, page_title: document.title });
    }
    setPrefs(p);
    setVisible(false);
    setOpen(false);
  };

  return (
    <>
      {visible && (
        <div
          data-testid="cookie-banner"
          className="fixed bottom-0 inset-x-0 z-[60] bg-bone/95 backdrop-blur-md border-t border-ink/15"
        >
          <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-6 md:py-7 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-12">
            <p className="text-sm font-light leading-relaxed text-ink/80 max-w-3xl">
              Vi bruger nødvendige cookies for at få hjemmesiden til at fungere. Med dit samtykke
              bruger vi også cookies til statistik og marketing. Du kan til enhver tid ændre dit
              valg under “Cookieindstillinger” nederst på siden. Dit valg gemmes i 12 måneder. Læs mere i vores{' '}
              <Link data-testid="cookie-banner-policy-link" to="/cookiepolitik" className="underline underline-offset-4">
                cookiepolitik
              </Link>
              .
            </p>
            <div className="flex flex-wrap gap-3 lg:ml-auto shrink-0">
              <button data-testid="cookie-customize-btn" className={ghostBtn} onClick={() => setOpen(true)}>
                Tilpas
              </button>
              <button
                data-testid="cookie-reject-btn"
                className={solidBtn}
                onClick={() => decide({ analytics: false, marketing: false })}
              >
                Afvis
              </button>
              <button
                data-testid="cookie-accept-btn"
                className={solidBtn}
                onClick={() => decide({ analytics: true, marketing: true })}
              >
                Accepter
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cookie-settings-dialog" className="bg-bone rounded-none border-ink/15 max-w-lg">
          <DialogTitle className="font-serif font-light text-3xl">Cookieindstillinger</DialogTitle>
          <DialogDescription className="text-sm font-light text-ink/70">
            Vælg hvilke typer cookies du vil tillade.
          </DialogDescription>
          <div className="mt-2 divide-y divide-ink/10">
            {categories.map((c) => (
              <div key={c.key} className="py-5 flex items-start justify-between gap-6">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-ink">{c.title}</div>
                  <p className="mt-2 text-sm font-light text-ink/70 leading-relaxed">{c.text}</p>
                </div>
                <Switch
                  data-testid={`cookie-switch-${c.key}`}
                  checked={c.locked ? true : prefs[c.key]}
                  disabled={c.locked}
                  onCheckedChange={(v) => setPrefs({ ...prefs, [c.key]: v })}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 justify-end pt-2">
            <button
              data-testid="cookie-save-btn"
              className={ghostBtn}
              onClick={() => decide(prefs)}
            >
              Gem valg
            </button>
            <button
              data-testid="cookie-reject-all-btn"
              className={solidBtn}
              onClick={() => decide({ analytics: false, marketing: false })}
            >
              Afvis alle
            </button>
            <button
              data-testid="cookie-accept-all-btn"
              className={solidBtn}
              onClick={() => decide({ analytics: true, marketing: true })}
            >
              Accepter alle
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieBanner;
